/* ─────────────────────────────────────────────────────────────
   Medicore announcement bar

   Rotates messages with a slide or fade. Autoplay pauses on hover,
   on keyboard focus and whenever the tab is hidden, so a visitor
   reading a message never has it pulled away.

   Registered as a custom element so the theme editor gets a working
   bar the moment the section is added, and each instance cleans up
   its own timer when removed.
   ───────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  var STORAGE_PREFIX = 'mc-announce-dismissed:';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function readDismissed(signature) {
    try {
      return window.localStorage.getItem(STORAGE_PREFIX + signature) === '1';
    } catch (e) {
      // Private mode or blocked storage: treat as not dismissed.
      return false;
    }
  }

  function writeDismissed(signature) {
    try {
      window.localStorage.setItem(STORAGE_PREFIX + signature, '1');
    } catch (e) {
      /* nothing to do - the bar simply returns next visit */
    }
  }

  var McAnnouncement = function () {};

  if (typeof HTMLElement === 'function') {
    McAnnouncement = class extends HTMLElement {
      connectedCallback() {
        this.slides = Array.prototype.slice.call(
          this.querySelectorAll('[data-mc-an-slide]')
        );
        if (!this.slides.length) return;

        this.track = this.querySelector('[data-mc-an-track]');
        this.viewport = this.querySelector('[data-mc-an-viewport]');
        this.effect = this.dataset.effect || 'slide';
        this.index = 0;
        this.timer = null;
        this.paused = false;

        if (this.dataset.dismissible === 'true' && readDismissed(this.dataset.signature)) {
          this.hidden = true;
          return;
        }

        this.bind();
        this.measure();
        this.apply();
        this.start();
      }

      disconnectedCallback() {
        this.stop();
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      }

      bind() {
        var self = this;

        var prev = this.querySelector('[data-mc-an-prev]');
        var next = this.querySelector('[data-mc-an-next]');
        if (prev) prev.addEventListener('click', function () { self.step(-1, true); });
        if (next) next.addEventListener('click', function () { self.step(1, true); });

        var close = this.querySelector('[data-mc-an-close]');
        if (close) {
          close.addEventListener('click', function () {
            self.stop();
            self.hidden = true;
            writeDismissed(self.dataset.signature);
          });
        }

        if (this.dataset.pauseOnHover === 'true') {
          this.addEventListener('mouseenter', function () { self.paused = true; });
          this.addEventListener('mouseleave', function () { self.paused = false; });
        }

        // Keyboard focus always pauses, regardless of the hover setting.
        this.addEventListener('focusin', function () { self.paused = true; });
        this.addEventListener('focusout', function () { self.paused = false; });

        this.onVisibility = function () {
          self.paused = document.hidden;
        };
        document.addEventListener('visibilitychange', this.onVisibility);

        this.onResize = function () {
          self.measure();
          self.apply(true);
        };
        window.addEventListener('resize', this.onResize);
      }

      // Vertical sliding needs an explicit viewport height; the slides
      // are variable-height text, so measure the tallest one.
      measure() {
        if (this.effect !== 'slide-vertical' || !this.viewport) return;
        var tallest = 0;
        this.slides.forEach(function (slide) {
          tallest = Math.max(tallest, slide.offsetHeight);
        });
        if (tallest) this.viewport.style.height = tallest + 'px';
      }

      apply(instant) {
        var self = this;
        var speed = instant || reduceMotion ? '1ms' : '';

        this.slides.forEach(function (slide, i) {
          var active = i === self.index;
          slide.classList.toggle('is-active', active);
          if (active) slide.removeAttribute('aria-hidden');
          else slide.setAttribute('aria-hidden', 'true');
        });

        if (this.effect === 'fade' || !this.track) return;

        if (speed) this.track.style.transitionDuration = speed;
        else this.track.style.transitionDuration = '';

        var offset = this.index * 100;
        this.track.style.transform =
          this.effect === 'slide-vertical'
            ? 'translateY(-' + offset + '%)'
            : 'translateX(-' + offset + '%)';
      }

      step(direction, fromUser) {
        var total = this.slides.length;
        this.index = (this.index + direction + total) % total;
        this.apply();
        if (fromUser) this.restart();
      }

      start() {
        if (this.dataset.autoplay !== 'true') return;
        if (this.slides.length < 2) return;

        var self = this;
        var interval = parseInt(this.dataset.interval, 10);
        if (isNaN(interval) || interval < 1500) interval = 5000;

        this.stop();
        this.timer = setInterval(function () {
          if (!self.paused) self.step(1, false);
        }, interval);
      }

      stop() {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      }

      restart() {
        this.stop();
        this.start();
      }
    };

    if (!customElements.get('mc-announcement')) {
      customElements.define('mc-announcement', McAnnouncement);
    }
  }
})();
